import React from 'react';
import Switch from '@mui/material/Switch';
import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { SectionCard } from '../../ui/SettingsUi';
import { NotificationsRow } from './NotificationsRow';
import { getByPath } from './notificationsEmailUtils';
import { useMe } from 'core/me/provider/MeProvider';
import { useStaffSplitLists } from 'sectors/pilates/staff/hooks/useStaffList';
import { notificationsEmailApi } from './api/notificationsEmailApi';
import Chip from '@mui/material/Chip';
import TestMailModal from './modals/TestMailModal';

function pillButtonSx(theme, { disabled }) {
  const isDark = theme.palette.mode === 'dark';

  const border = isDark ? alpha('#fff', 0.16) : alpha('#0b1220', 0.14);
  const bg = isDark ? alpha('#fff', 0.06) : alpha('#0b1220', 0.04);
  const hoverBg = isDark ? alpha('#fff', 0.09) : alpha('#0b1220', 0.06);

  return {
    height: 32,
    minHeight: 32,
    borderRadius: 999,
    px: 1.15,
    fontWeight: 900,
    textTransform: 'none',
    border: `1px solid ${border}`,
    background: bg,
    boxShadow: isDark ? '0 8px 18px rgba(0,0,0,0.22)' : '0 8px 18px rgba(15,23,42,0.08)',
    transition: 'all 140ms ease',
    ...(disabled
      ? { opacity: 0.6 }
      : {
          '&:hover': {
            background: hoverBg,
            transform: 'translateY(-1px)',
            boxShadow: isDark ? '0 10px 22px rgba(0,0,0,0.28)' : '0 10px 22px rgba(15,23,42,0.12)',
          },
          '&:active': { transform: 'translateY(0)' },
        }),
  };
}

function normalizeStaffOptions(workingList, passiveList) {
  const all = [...(workingList || []), ...(passiveList || [])];
  const map = new Map();

  all.forEach((s) => {
    if (!s?.id) return;

    const fullName =
      s.fullName || s.name || `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || `#${s.id}`;

    map.set(String(s.id), {
      id: s.id,
      name: fullName,
      email: (s.email || '').trim(),
      working: !!s.working,
      active: s.active !== false,
    });
  });

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
}

export function EmailAutomationsSection({ draft, dirtyBadge, patch }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const { me } = useMe();
  const role = (me?.membershipRole || '').toUpperCase();
  const canTest = role === 'OWNER' || role === 'ADMIN';

  const emailEnabled = !!getByPath(draft, 'enabled');
  const morningPlanEnabled = !!getByPath(draft, 'morning.sendGeneralPlanToAllStaff');

  const shellBorder = alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1);
  const boxBg = isDark ? alpha('#0b1220', 0.16) : alpha('#0b1220', 0.02);

  const blockSx = {
    p: 1,
    borderRadius: '14px',
    border: `1px solid ${shellBorder}`,
    bgcolor: boxBg,
    opacity: emailEnabled ? 1 : 0.6,
    pointerEvents: emailEnabled ? 'auto' : 'none',
    transition: 'opacity .15s ease',
  };

  const blockTitleSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    fontWeight: 900,
    fontSize: 13,
    mb: 0.5,
  };

  const switchProps = { size: 'small', disabled: !emailEnabled };

  // staff lists
  const { workingList, passiveList, isLoading: staffLoading } = useStaffSplitLists();
  const staffOptions = React.useMemo(
    () => normalizeStaffOptions(workingList, passiveList),
    [workingList, passiveList]
  );

  const defaultStaffId = me?.sector?.staffId ?? null;

  // modal + check/test state
  const [testOpen, setTestOpen] = React.useState(false);
  const [testing, setTesting] = React.useState(false);

  const [check, setCheck] = React.useState(null);
  const [checkLoading, setCheckLoading] = React.useState(false);

  // ✅ modal içinde gösterilecek backend sonucu (message burada)
  const [testResult, setTestResult] = React.useState(null);

  const refreshCheck = React.useCallback(async () => {
    setCheckLoading(true);
    try {
      const res = await notificationsEmailApi.checkMorningPlan();
      setCheck(res);
      return res;
    } catch (e) {
      setCheck({ enabled: false, blockingErrors: ['CHECK_FAILED'], warnings: [] });
      throw e;
    } finally {
      setCheckLoading(false);
    }
  }, []);

  const openTest = async () => {
    setTestOpen(true);
    setTestResult(null);
    try {
      await refreshCheck();
    } catch (e) {
      console.error(e);
    }
  };

  const closeTest = () => {
    setTestOpen(false);
    setCheck(null);
    setTestResult(null);
  };

  const handleTestConfirm = async ({ toEmail, staffId }) => {
    setTesting(true);
    try {
      const payload = {
        day: new Date().toISOString().slice(0, 10),
        staffId,
        toEmail,
      };

      const res = await notificationsEmailApi.testMorningPlan(payload);

      // ✅ artık modalda res.message gösterilecek
      setTestResult(res);

      // ✅ check’i güncelle (backend tek kaynak)
      if (res?.blockingErrors || res?.warnings) {
        // backend yeni formatta döndürüyor olabilir
        setCheck((prev) => ({
          ...(prev || {}),
          blockingErrors: res.blockingErrors || [],
          warnings: res.warnings || [],
          enabled: !!res.ok,
        }));
      } else if (res?.check) {
        setCheck(res.check);
      }
    } catch (e) {
      console.error('Test morning plan failed', e);
      setTestResult({
        status: 'TEST_FAILED',
        ok: false,
        blockingErrors: ['TEST_FAILED'],
        warnings: [],
        hasSessions: false,
        sessionCount: 0,
        message: e?.message || 'Test başarısız.',
      });
    } finally {
      setTesting(false);
    }
  };

  const testDisabled = testing || staffLoading || checkLoading;
  const softColBg = isDark ? alpha('#0b1220', 0.2) : alpha('#fff', 0.6);
  const comingSoonChip = (
    <Chip
      label="Yakında"
      size="small"
      sx={(theme) => ({
        height: 22,
        fontWeight: 900,
        borderRadius: 999,
        px: 0.75,
        bgcolor: alpha(theme.palette.warning.main, 0.14),
        color: theme.palette.warning.dark,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
      })}
    />
  );
  return (
    <SectionCard
      icon="schedule_send"
      title="Email Otomasyonları"
      subtitle="Sabah/akşam özetler ve uyarılar"
      right={dirtyBadge}
    >
      <MDBox
        sx={{
          p: 1.1,
          borderRadius: '16px',
          border: `1px solid ${shellBorder}`,
          bgcolor: softColBg,
        }}
      >
        <NotificationsRow
          label="Email bildirimleri aktif"
          hint="Bu kapalıysa hiçbir mail gönderilmez"
          right={
            <Switch checked={emailEnabled} onChange={(e) => patch('enabled', e.target.checked)} />
          }
        />
      </MDBox>

      <MDBox sx={{ display: 'grid', gap: 1 }}>
        {/* Morning */}
        <MDBox sx={blockSx}>
          <MDTypography sx={blockTitleSx}>
            <Icon sx={{ fontSize: 18, opacity: 0.85 }}>wb_sunny</Icon>
            Sabah
          </MDTypography>

          <NotificationsRow
            label="Herkese genel planlama maili"
            hint="Tüm personele günün program özeti"
            right={
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  {...switchProps}
                  checked={morningPlanEnabled}
                  onChange={(e) => {
                    patch('morning.sendGeneralPlanToAllStaff', e.target.checked);
                    setCheck(null);
                    setTestResult(null);
                  }}
                />

                {canTest && (
                  <>
                    <Button
                      onClick={openTest}
                      disabled={testDisabled}
                      startIcon={
                        <Icon sx={{ fontSize: 18 }}>
                          {testing || checkLoading ? 'hourglass_top' : 'play_arrow'}
                        </Icon>
                      }
                      sx={pillButtonSx(theme, { disabled: testDisabled })}
                    >
                      Test et
                    </Button>

                    <TestMailModal
                      open={testOpen}
                      onClose={closeTest}
                      defaultEmail={me?.email || ''}
                      staffOptions={staffOptions}
                      defaultStaffId={defaultStaffId}
                      blockingErrors={check?.blockingErrors || []}
                      warnings={check?.warnings || []}
                      loading={checkLoading || testing}
                      onConfirm={handleTestConfirm}
                      testResult={testResult} // ✅ message burada gösterilecek
                    />
                  </>
                )}
              </Stack>
            }
          />

          <NotificationsRow
            label="Yöneticiye genel planlama maili"
            hint="Bugünkü seanslar / doluluk / eğitmen dağılımı"
            right={
              <Stack direction="row" spacing={1} alignItems="center">
                {comingSoonChip}
                <Switch {...switchProps} disabled checked={false} onChange={() => {}} />
              </Stack>
            }
          />
        </MDBox>

        {/* Evening */}
        <MDBox sx={blockSx}>
          <MDTypography sx={blockTitleSx}>
            <Icon sx={{ fontSize: 18, opacity: 0.85 }}>nightlight</Icon>
            Akşam
          </MDTypography>

          <NotificationsRow
            label="Şirket sahiplerine gün sonu özeti"
            hint="Günün seansları + gerçekleşenler"
            right={
              <Stack direction="row" spacing={1} alignItems="center">
                {comingSoonChip}
                <Switch {...switchProps} disabled checked={false} onChange={() => {}} />
              </Stack>
            }
          />

          <NotificationsRow
            label="Yöneticiye eğitmen eylemleri gönderilsin"
            hint="Ödeme aldı / seans sildi / vb. özet"
            right={
              <Switch
                {...switchProps}
                checked={!!getByPath(draft, 'evening.sendOwnerMemberLogs')}
                onChange={(e) => patch('evening.sendOwnerMemberLogs', e.target.checked)}
              />
            }
          />

          <NotificationsRow
            label="Eğitmenlere yoklama girilmedi uyarısı"
            hint="Gün bittiğinde yoklama eksikse mail at"
            right={
              <Switch
                {...switchProps}
                checked={!!getByPath(draft, 'evening.sendTrainerMissingAttendanceAlert')}
                onChange={(e) =>
                  patch('evening.sendTrainerMissingAttendanceAlert', e.target.checked)
                }
              />
            }
          />

          <NotificationsRow
            label="Yönetciye yoklama girmeyen eğitmenler listesi"
            hint="Uyarı mailinde personel bazlı liste"
            right={
              <Switch
                {...switchProps}
                checked={!!getByPath(draft, 'evening.showMissingStaffList')}
                onChange={(e) => patch('evening.showMissingStaffList', e.target.checked)}
              />
            }
          />
        </MDBox>
      </MDBox>
    </SectionCard>
  );
}
